import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { EsignService } from 'src/app/esign/esign.service';
import { esignActions } from '../actions';
import { WINDOW } from 'src/app/core/core.module';
import { Store } from '@ngrx/store';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';

@Injectable()
export class EsignEffects {
    constructor(
        @Inject(WINDOW) private readonly _window: Window,
        private readonly _actions$: Actions,
        private readonly _esignService: EsignService,
        private readonly _store: Store<{}>
    ) {}

    getTemplateImage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.getTemplateImage),
            switchMap((action) =>
                this._esignService.getTemplateImage(action.externalTemplateId).pipe(
                    map((image) =>
                        esignActions.EsignActions.getTemplateImageSuccess({ templateName: action.templateName, image })
                    ),
                    catchError((error) => of(esignActions.EsignActions.getTemplateImageFailure({ error })))
                )
            )
        )
    );

    getTemplateImageSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(esignActions.EsignActions.getTemplateImageSuccess),
                tap((action) => {
                    const image = new Image();
                    image.src = 'data:image/png;base64,' + action.image;

                    const w = this._window.open('');
                    if (w) {
                        w.document.write(image.outerHTML);
                        setTimeout(() => {
                            w.document.title = action.templateName;
                        }, 0);
                        w.document.close();
                    }
                })
            ),
        { dispatch: false }
    );

    loadCandidateInfo$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.getCandidateInfo),
            switchMap((action) =>
                this._esignService.getCandidateInfo(action.contractId).pipe(
                    map((candidateInfo) => esignActions.EsignActions.getCandidateInfoSuccess({ candidateInfo })),
                    catchError((error) => of(esignActions.EsignActions.getCandidateInfoFailure({ error })))
                )
            )
        )
    );

    loadRequiredDocuments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.getRequiredDocuments),
            switchMap((action) =>
                this._esignService.getRequiredDocuments(action.contractId).pipe(
                    map((requiredDocuments) =>
                        esignActions.EsignActions.getRequiredDocumentsSuccess({ requiredDocuments })
                    ),
                    catchError((error) => of(esignActions.EsignActions.getRequiredDocumentsFailure({ error })))
                )
            )
        )
    );

    getTemplateFolders$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.getTemplateFolders),
            switchMap((action) =>
                this._esignService.getTemplateFolders(action.forceRefresh).pipe(
                    map((folders) => esignActions.EsignActions.getTemplateFoldersSuccess({ folders })),
                    catchError((error) => of(esignActions.EsignActions.getTemplateFoldersFailure({ error })))
                )
            )
        )
    );

    searchTemplates$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.searchTemplates),
            switchMap((action) =>
                this._esignService.searchTemplates(action.folderId, action.name).pipe(
                    map((templates) => esignActions.EsignActions.searchTemplatesSuccess({ templates })),
                    catchError((error) => of(esignActions.EsignActions.searchTemplatesFailure({ error })))
                )
            )
        )
    );

    associateTemplateDocumentType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.associateTemplateDocumentType),
            switchMap((action) =>
                this._esignService
                    .associateTemplateDocumentType(action.template, action.documentTypeId, action.contractId)
                    .pipe(
                        map((response) =>
                            esignActions.EsignActions.associateTemplateDocumentTypeSuccess({
                                template: action.template,
                                documentTypeId: action.documentTypeId,
                                templateId: response.templateId,
                                stateId: response.stateId
                            })
                        ),
                        catchError((error) =>
                            of(
                                esignActions.EsignActions.associateTemplateDocumentTypeFailure({
                                    error,
                                    contractId: action.contractId
                                })
                            )
                        )
                    )
            )
        )
    );

    associateTemplateDocumentTypeAdHoc$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.associateTemplateDocumentTypeAdHoc),
            switchMap((action) =>
                this._esignService
                    .associateTemplateDocumentTypeAdHoc(action.template, action.documentTypeId, action.contractId)
                    .pipe(
                        map((response) =>
                            esignActions.EsignActions.associateTemplateDocumentTypeAdHocSuccess({
                                template: action.template,
                                documentTypeId: action.documentTypeId,
                                templateId: response.templateId,
                                stateId: response.stateId
                            })
                        ),
                        catchError((error) =>
                            of(
                                esignActions.EsignActions.associateTemplateDocumentTypeAdHocFailure({
                                    error,
                                    contractId: action.contractId
                                })
                            )
                        )
                    )
            )
        )
    );

    disassociateTemplateDocumentType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.disassociateTemplateDocumentType),
            switchMap((action) =>
                this._esignService
                    .disassociateTemplateDocumentType(action.internalTemplateId, action.documentTypeId, action.stateId)
                    .pipe(
                        map(() =>
                            esignActions.EsignActions.disassociateTemplateDocumentTypeSuccess({
                                documentTypeId: action.documentTypeId
                            })
                        ),
                        catchError((error) => {
                            return of(
                                esignActions.EsignActions.disassociateTemplateDocumentTypeFailure({
                                    error: error,
                                    contractId: action.contractId
                                })
                            );
                        })
                    )
            )
        )
    );

    templateDocumentTypeAssociationFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                esignActions.EsignActions.disassociateTemplateDocumentTypeFailure,
                esignActions.EsignActions.associateTemplateDocumentTypeFailure,
                esignActions.EsignActions.associateTemplateDocumentTypeAdHocFailure
            ),
            map((result) => esignActions.EsignActions.getEsignDocuments({ contractId: result.contractId }))
        )
    );

    sendEnvelopeType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.sendEnvelope),
            switchMap((action) =>
                this._esignService.sendEnvelope(action.recipient).pipe(
                    map((r) => {
                        this._store.dispatch(
                            esignActions.EsignActions.getEnvelope({ envelopeId: Number(r.headers.get('Location')) })
                        );
                        this._store.dispatch(showToasterMessage({ message: 'Envelope sent to Docusign' }));
                        return esignActions.EsignActions.sendEnvelopeSuccess();
                    }),
                    catchError((error) => of(esignActions.EsignActions.sendEnvelopeFailure({ error })))
                )
            )
        )
    );

    sendEnvelopeTypeSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.sendEnvelopeSuccess),
            map((result) => esignActions.EsignActions.setCreateMode({ isCreateMode: false }))
        )
    );

    templateSearchComplete$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.searchTemplatesSuccess),
            map((result) => esignActions.EsignActions.setTemplateSearchComplete({ isTemplateSearchComplete: false }))
        )
    );

    getEnvelopeFromLocation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.getEnvelope),
            switchMap((action) =>
                this._esignService.getEnvelope(action.envelopeId).pipe(
                    map((envelope) => esignActions.EsignActions.getEnvelopeSuccess({ envelope })),
                    catchError((error) => of(esignActions.EsignActions.getEnvelopeFailure({ error })))
                )
            )
        )
    );

    getContractEnvelopes$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.getContractEnvelopes),
            switchMap((action) =>
                this._esignService.getContractEnvelopes(action.contractId).pipe(
                    map((envelopes) => esignActions.EsignActions.getContractEnvelopesSuccess({ envelopes })),
                    catchError((error) => of(esignActions.EsignActions.getContractEnvelopesFailure({ error })))
                )
            )
        )
    );

    getPortalEnvelopes$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.getPortalEnvelopes),
            exhaustMap((action) =>
                this._esignService.getPortalEnvelopes(action.candidateId).pipe(
                    map((envelopes) => esignActions.EsignActions.getPortalEnvelopesSuccess({ envelopes })),
                    catchError((error) => of(esignActions.EsignActions.getPortalEnvelopesFailure({ error })))
                )
            )
        )
    );

    getEsignDocuments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.getEsignDocuments),
            switchMap((action) =>
                this._esignService.getEsignDocuments(action.contractId).pipe(
                    map((documents) => esignActions.EsignActions.getEsignDocumentsSuccess({ documents })),
                    catchError((error) => of(esignActions.EsignActions.getEsignDocumentsFailure({ error })))
                )
            )
        )
    );

    error$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    esignActions.EsignActions.getCandidateInfoFailure,
                    esignActions.EsignActions.getTemplateFoldersFailure,
                    esignActions.EsignActions.searchTemplatesFailure,
                    esignActions.EsignActions.getRequiredDocumentsFailure,
                    esignActions.EsignActions.getTemplateImageFailure,
                    esignActions.EsignActions.disassociateTemplateDocumentTypeFailure,
                    esignActions.EsignActions.sendEnvelopeFailure,
                    esignActions.EsignActions.getEnvelopeFailure,
                    esignActions.EsignActions.getContractEnvelopesFailure,
                    esignActions.EsignActions.getEsignDocumentsFailure,
                    esignActions.EsignActions.getPortalEnvelopesFailure,
                    esignActions.EsignActions.associateTemplateDocumentTypeFailure
                ),
                tap((action: any) => {
                    if (action.error?.status && action.error?.status < 500) {
                        this._store.dispatch(showToasterMessage({ message: action.error?.error }));
                    } else {
                        this._store.dispatch(
                            showToasterMessage({ message: 'The system has encountered an error. Please try again.' })
                        );
                    }
                })
            ),
        { dispatch: false }
    );

    cancelEnvelopeType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.cancelEnvelope),
            switchMap((action) =>
                this._esignService.cancelEnvelope(action.envelopeId).pipe(
                    map(() =>
                        esignActions.EsignActions.cancelEnvelopeSuccess({
                            envelopeId: action.envelopeId,
                            contractId: action.contractId
                        })
                    ),
                    catchError((error) => of(esignActions.EsignActions.cancelEnvelopeFailure({ error })))
                )
            )
        )
    );

    cancelEnvelopeSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(esignActions.EsignActions.cancelEnvelopeSuccess),
                tap((action) => {
                    this._store.dispatch(showToasterMessage({ message: 'Envelope canceled!' }));
                    this._store.dispatch(
                        esignActions.EsignActions.getContractEnvelopes({ contractId: action.contractId })
                    );
                })
            ),
        { dispatch: false }
    );

    resendEnvelope$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.resendEnvelope),
            switchMap((action) =>
                this._esignService.resendEnvelope(action.internalEnvelopeId).pipe(
                    map(() => esignActions.EsignActions.resendEnvelopeSuccess()),
                    catchError((error) => of(esignActions.EsignActions.resendEnvelopeFailure({ error })))
                )
            )
        )
    );

    resendEnvelopeSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.resendEnvelopeSuccess),
            map((action) => showToasterMessage({ message: 'Envelope resent to candidate' }))
        )
    );

    getPermissions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(esignActions.EsignActions.getEsignPermissions),
            switchMap((action) =>
                this._esignService.getPermissions().pipe(
                    map((esignPermissions) =>
                        esignActions.EsignActions.getEsignPermissionsSuccess({ esignPermissions })
                    ),
                    catchError((error) => of(esignActions.EsignActions.getEsignPermissionsFailure({ error })))
                )
            )
        )
    );
}
